import { type JudgeOutcome, type JudgeSummaryResponse } from '../../services/api/judge';
import { cn } from '../../utils/cn';

import { OUTCOME_BAR_COLORS, OUTCOME_LABELS, OUTCOME_TEXT_COLORS } from './kvalitet-labels';

interface KvalitetOutcomeCardsProps {
  byOutcome: JudgeSummaryResponse['byOutcome'];
  totals: JudgeSummaryResponse['totals'];
  selected: readonly JudgeOutcome[];
  onSelect: (outcome: JudgeOutcome) => void;
}

export const KvalitetOutcomeCards = ({
  byOutcome,
  totals,
  selected,
  onSelect,
}: KvalitetOutcomeCardsProps) => {
  const { judged } = totals;
  const decided = judged - totals.notApplicable;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Utfall</h3>
        <div className="flex items-baseline gap-6 text-sm text-slate-600">
          <span>
            Bedömda{' '}
            <strong className="text-lg text-slate-900">{judged.toLocaleString('sv-SE')}</strong>
          </span>
          <span title={`${totals.resolved} av ${decided} med något att lösa`}>
            Löst-andel{' '}
            <strong className="text-lg text-emerald-700">
              {totals.resolvedRate === null ? '–' : `${Math.round(totals.resolvedRate * 100)} %`}
            </strong>
          </span>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {byOutcome.map(({ outcome, count }) => {
          const pct = judged > 0 ? Math.round((count / judged) * 100) : 0;
          const active = selected.length === 1 && selected[0] === outcome;
          return (
            <button
              key={outcome}
              type="button"
              onClick={() => onSelect(outcome)}
              aria-pressed={active}
              className={cn(
                'rounded-xl border bg-white p-4 text-left shadow-sm transition hover:border-slate-400',
                active ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200',
              )}
            >
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                {OUTCOME_LABELS[outcome]}
              </div>
              <div className={cn('text-2xl font-bold', OUTCOME_TEXT_COLORS[outcome])}>
                {count.toLocaleString('sv-SE')}
              </div>
              <div className="text-xs text-slate-500 mt-1">{pct} % av bedömda</div>
              <div className="mt-3 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={cn('h-full rounded-full', OUTCOME_BAR_COLORS[outcome])}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
